const WIDE_SCREEN = '(min-width: 64rem)';

/**
 * Onglets des pages Formation (programme des fiches à plusieurs niveaux, FAQ) :
 * onglets sur écran large (ou à toutes les largeurs avec data-tabs="always"), cartes empilées
 * en dessous (et à l'impression, ou sans script).
 * Le conteneur reçoit data-ready, qui active l'affichage en onglets côté CSS ;
 * un niveau non choisi porte data-inactive. Le clavier suit le modèle d'onglets
 * WAI-ARIA (flèches, Début, Fin) et l'ancre de l'adresse ouvre le niveau visé.
 */
export function initProgrammeTabs(): void {
    document.querySelectorAll<HTMLElement>('[data-tabs]').forEach((root) => {
        const tabs = Array.from(root.querySelectorAll<HTMLButtonElement>('[data-tab]'));
        const panels = tabs.map((tab) => document.getElementById(tab.getAttribute('aria-controls') ?? ''));
        if (tabs.length === 0 || panels.some((panel) => panel === null)) {
            return;
        }
        const tabPanels = panels as HTMLElement[];
        // data-tabs="always" : onglets à toutes les largeurs (sinon sur écran large seulement)
        const wide = window.matchMedia(root.dataset.tabs === 'always' ? 'all' : WIDE_SCREEN);

        const select = (index: number, fromUser = false): void => {
            tabs.forEach((tab, i) => {
                const active = i === index;
                tab.setAttribute('aria-selected', String(active));
                tab.tabIndex = active ? 0 : -1;
                tabPanels[i].toggleAttribute('data-inactive', !active);
            });
            // Barre défilante quand les onglets ne tiennent pas : l'onglet choisi reste visible
            tabs[index].scrollIntoView({ block: 'nearest', inline: 'nearest' });
            if (fromUser) {
                tabs[index].focus();
                // Adresse partageable sans saut de page
                history.replaceState(null, '', `#${tabPanels[index].id}`);
            }
        };

        // Les rôles d'onglet ne valent que lorsque la barre d'onglets est affichée
        const applyRoles = (): void => {
            tabPanels.forEach((panel, i) => {
                if (wide.matches) {
                    panel.setAttribute('role', 'tabpanel');
                    panel.setAttribute('aria-labelledby', tabs[i].id);
                } else {
                    panel.removeAttribute('role');
                    panel.removeAttribute('aria-labelledby');
                }
            });
        };

        const openFromHash = (): void => {
            const id = decodeURIComponent(window.location.hash.slice(1));
            const index = tabPanels.findIndex((panel) => panel.id === id);
            if (index < 0) {
                return;
            }
            select(index);
            (wide.matches ? root : tabPanels[index]).scrollIntoView();
        };

        tabs.forEach((tab, index) => {
            tab.addEventListener('click', () => select(index, true));
            tab.addEventListener('keydown', (event) => {
                const last = tabs.length - 1;
                const keys: Record<string, number> = {
                    ArrowRight: index === last ? 0 : index + 1,
                    ArrowLeft: index === 0 ? last : index - 1,
                    Home: 0,
                    End: last,
                };
                const target = keys[event.key];
                if (target === undefined) {
                    return;
                }
                event.preventDefault();
                select(target, true);
            });
        });

        root.setAttribute('data-ready', '');
        applyRoles();
        wide.addEventListener('change', applyRoles);
        openFromHash();
        window.addEventListener('hashchange', openFromHash);
    });
}
